// apps/client/src/components/AdsgramTask.tsx
//
// Wrapper for <adsgram-task> web component
// Per Adsgram docs: Task ads use a web component, NOT init()/show()
// Docs: https://docs.adsgram.ai/publisher/task-integration-example
//

import { useEffect, useRef, useCallback } from 'react';
import { TASK_BLOCK_ID } from '../lib/adsgram';

// Extend JSX to recognize <adsgram-task> as an intrinsic element
declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements {
            'adsgram-task': React.DetailedHTMLProps<
                React.HTMLAttributes<HTMLElement> & {
                    'data-block-id'?: string;
                    'data-debug'?: string;
                    'data-debug-console'?: string;
                },
                HTMLElement
            >;
        }
    }
}

interface AdsgramTaskProps {
    /** Called when user completes the task and earns reward */
    onReward?: () => void;
    /** Called when an error occurs */
    onError?: (error: unknown) => void;
    /** Called when no banner/task is available */
    onBannerNotFound?: () => void;
    /** Additional CSS class */
    className?: string;
}

export function AdsgramTask({ onReward, onError, onBannerNotFound, className }: AdsgramTaskProps) {
    const ref = useRef<HTMLElement>(null);

    const handleReward = useCallback(() => {
        console.log('🎯 Task ad completed — reward earned');
        onReward?.();
    }, [onReward]);

    const handleError = useCallback((e: Event) => {
        console.error('❌ Task ad error:', e);
        onError?.(e);
    }, [onError]);

    const handleNotFound = useCallback(() => {
        console.log('⚠️ No task ad available');
        onBannerNotFound?.();
    }, [onBannerNotFound]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        el.addEventListener('reward', handleReward);
        el.addEventListener('onError', handleError);
        el.addEventListener('onBannerNotFound', handleNotFound);

        return () => {
            el.removeEventListener('reward', handleReward);
            el.removeEventListener('onError', handleError);
            el.removeEventListener('onBannerNotFound', handleNotFound);
        };
    }, [handleReward, handleError, handleNotFound]);

    return (
        <adsgram-task
            ref={ref}
            data-block-id={TASK_BLOCK_ID}
            data-debug="false"
            data-debug-console="false"
            className={className}
        />
    );
}
